/**
 * Formats the service name to the Datadog RUM accepted tag format.
 * References: https://docs.datadoghq.com/getting_started/tagging/#define-tags
 *
 * Requirements:
 * - Must start with a letter
 * - Allowed characters: alphanumerics, underscores, minuses, colons, periods, slashes
 * - CamelCase is converted to lowercase (TestTag -> test_tag)
 * - Other special characters are converted to underscores
 * - Max length: 200 characters
 *
 * @param serviceName - The service name to format
 * @returns Formatted service name for Datadog
 */
export const formatDatadogServiceName = (serviceName: string): string => {
  if (!serviceName || typeof serviceName !== 'string') {
    return '';
  }

  // Convert to lowercase
  let formatted = serviceName.toLowerCase();

  // Convert CamelCase to snake_case
  formatted = formatted.replace(/([a-z])([A-Z])/g, '$1_$2').toLowerCase();

  // Replace spaces with underscores
  formatted = formatted.replace(/\s+/g, '_');

  // Remove special characters (keeps: alphanumerics, _, -, :, ., /)
  formatted = formatted.replace(/[^a-z0-9_\-.:\/]/g, '_');

  // Collapse consecutive underscores
  formatted = formatted.replace(/_+/g, '_');

  // Trim underscores from start/end (avoiding regex backtracking)
  while (formatted.startsWith('_')) {
    formatted = formatted.slice(1);
  }
  while (formatted.endsWith('_')) {
    formatted = formatted.slice(0, -1);
  }

  // Ensure it starts with a letter (prefix if it starts with a number)
  if (formatted.length === 0 || !/^[a-z]/.test(formatted)) {
    formatted = `service_${formatted}`;
  }

  // Limit to 200 characters
  if (formatted.length > 200) {
    formatted = formatted.substring(0, 200);
  }

  return formatted;
};
